<template>
  <div class="individual-results">
    <div class="individual-results__event-selector">
      <select
        v-model="selectedEventGroupId"
        class="browser-default e4s-input-field e4s-input-field--primary individual-results__select"
        @change="onEventGroupChange"
      >
        <option :value="null">Select Event</option>
        <option v-for="item in sortedEventItems" :key="item.egId" :value="item.egId">
          {{ item.eventDefName }}
        </option>
      </select>
      <div v-if="cachedAt" class="individual-results__metadata">
        <span class="individual-results__updated">Updated: {{ formattedCachedAtShort }}</span>
        <button
          class="individual-results__refresh-btn"
          @click="refreshResults"
          :disabled="refreshing"
          aria-label="Refresh results"
        >
          <svg
            class="individual-results__refresh-icon"
            :class="{ 'individual-results__refresh-icon--spinning': refreshing }"
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M21 2v6h-6M3 12a9 9 0 0 1 15-6.7L21 8M3 22v-6h6M21 12a9 9 0 0 1-15 6.7L3 16" />
          </svg>
        </button>
      </div>
    </div>

    <div
      v-if="selectedEventGroupId && allRows.length > 0"
      class="individual-results__filter-wrapper"
    >
      <button
        type="button"
        class="individual-results__filter-toggle"
        @click="filtersExpanded = !filtersExpanded"
        :aria-expanded="filtersExpanded"
      >
        <span class="individual-results__filter-label">Filters</span>
        <svg
          class="individual-results__toggle-icon"
          :class="{ 'individual-results__toggle-icon--open': filtersExpanded }"
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
        </svg>
        <span class="individual-results__filter-summary">{{ filterSummary }}</span>
        <svg
          class="individual-results__chevron-icon"
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      <div
        v-show="filtersExpanded"
        class="individual-results__filters"
        :class="{ 'individual-results__filters--expanded': filtersExpanded }"
      >
        <div class="individual-results__filter-section">
          <span class="individual-results__filter-section-label">Gender</span>
          <div class="individual-results__segmented-group">
            <button
              type="button"
              class="individual-results__segmented-btn"
              :class="{ 'individual-results__segmented-btn--active': filterGender === 'all' }"
              @click="filterGender = 'all'"
            >
              All
            </button>
            <button
              type="button"
              class="individual-results__segmented-btn"
              :class="{ 'individual-results__segmented-btn--active': filterGender === 'M' }"
              @click="filterGender = 'M'"
            >
              M
            </button>
            <button
              type="button"
              class="individual-results__segmented-btn"
              :class="{ 'individual-results__segmented-btn--active': filterGender === 'F' }"
              @click="filterGender = 'F'"
            >
              F
            </button>
          </div>
        </div>

        <div class="individual-results__filter-section">
          <div class="individual-results__filter-section-header">
            <span class="individual-results__filter-section-label">Age groups</span>
            <span class="individual-results__selected-count"
              >{{ selectedAgeGroupCount }} selected</span
            >
          </div>
          <div class="individual-results__age-group-list">
            <button
              v-for="ag in availableAgeGroups"
              :key="ag"
              type="button"
              class="individual-results__age-group-btn"
              :class="{ 'individual-results__age-group-btn--active': filterAgeGroups.includes(ag) }"
              @click="toggleAgeGroup(ag)"
            >
              <svg
                v-if="filterAgeGroups.includes(ag)"
                class="individual-results__check-icon"
                viewBox="0 0 24 24"
                width="14"
                height="14"
                fill="none"
                stroke="currentColor"
                stroke-width="3"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span class="individual-results__age-group-label">{{ ag }}</span>
            </button>
          </div>
          <div class="individual-results__age-group-actions">
            <button
              type="button"
              class="individual-results__quick-action-btn"
              @click="selectAllAgeGroups"
              :disabled="allAgeGroupsSelected"
            >
              Select all
            </button>
            <button
              type="button"
              class="individual-results__quick-action-btn"
              @click="deselectAllAgeGroups"
              :disabled="noAgeGroupsSelected"
            >
              Deselect all
            </button>
          </div>
        </div>

        <div class="individual-results__actions">
          <span class="individual-results__count">
            Showing {{ athleteRows.length }} of {{ allRows.length }} athletes
          </span>
          <button
            v-if="filtersActive"
            class="individual-results__clear-btn e4s-button--small"
            @click="clearFilters"
          >
            Clear filters
          </button>
          <button
            v-if="badDataRows.length > 0"
            class="individual-results__bad-data-toggle e4s-button--small"
            @click="showBadData = !showBadData"
          >
            {{ showBadData ? 'Hide' : 'Show' }} {{ badDataRows.length }} with missing data
          </button>
        </div>
      </div>
    </div>

    <div v-if="loading" class="individual-results__loading">
      <p class="e4s-lite-table--standard-text">Loading results...</p>
    </div>

    <div v-if="error" class="individual-results__error">
      <p class="e4s-lite-table--standard-text">{{ error }}</p>
      <button class="e4s-button--primary" @click="() => loadResults()">Retry</button>
    </div>

    <div
      v-if="
        !loading && !error && athleteRows.length === 0 && selectedEventGroupId && allRows.length > 0
      "
      class="individual-results__empty"
    >
      <p class="e4s-lite-table--standard-text">No athletes match the selected filters</p>
      <p class="e4s-lite-table--standard-text individual-results__empty-hint">
        Try adjusting your gender or age group selection
      </p>
    </div>

    <div
      v-if="!loading && !error && allRows.length === 0 && selectedEventGroupId"
      class="individual-results__empty"
    >
      <p class="e4s-lite-table--standard-text">No results available for this event</p>
    </div>

    <div v-if="!loading && !error && athleteRows.length > 0" class="individual-results__body">
      <div class="individual-results__header e4s-flex-row e4s-gap--standard e4s-align-items-center">
        <div class="individual-results__col individual-results__col--athlete">Athlete</div>
        <div
          v-for="comp in compColumns"
          :key="comp.compId"
          class="individual-results__col individual-results__col--comp"
        >
          {{ formatDate(comp.date) }}
        </div>
        <div class="individual-results__col individual-results__col--total">Total</div>
      </div>

      <div
        v-for="(row, index) in athleteRows"
        :key="row.key"
        class="individual-results__row e4s-flex-row e4s-gap--standard e4s-align-items-center"
        :class="index % 2 === 1 ? 'individual-results__row--even' : ''"
      >
        <div class="individual-results__col individual-results__col--athlete">
          <div class="e4s-lite-table--standard-text">{{ row.athlete }}</div>
          <div class="individual-results__club">
            {{ row.club }}
            <span class="individual-results__meta">({{ row.gender }} / {{ row.agegroup }})</span>
          </div>
        </div>
        <div
          v-for="comp in compColumns"
          :key="comp.compId"
          class="individual-results__col individual-results__col--comp"
        >
          <template
            v-if="
              row.pointsByComp[comp.compId] !== undefined && row.pointsByComp[comp.compId] !== 0
            "
          >
            <span class="e4s-lite-number e4s-lite-number--points">
              {{ row.pointsByComp[comp.compId] }}
            </span>
            <span class="e4s-lite-number e4s-lite-number--score">
              {{ row.scoresByComp[comp.compId]?.toFixed(2) }}
            </span>
          </template>
        </div>
        <div class="individual-results__col individual-results__col--total">
          <span class="e4s-lite-number e4s-lite-number--score">{{ row.totalPoints }}</span>
        </div>
      </div>
    </div>

    <div
      v-if="showFilteredOut && filteredOutRows.length > 0"
      class="individual-results__filtered-out"
    >
      <div class="individual-results__filtered-header">
        Filtered out athletes ({{ filteredOutRows.length }})
      </div>
      <div
        v-for="item in filteredOutRows"
        :key="item.row.key"
        class="individual-results__row individual-results__row--filtered e4s-flex-row e4s-gap--standard e4s-align-items-center"
      >
        <div class="individual-results__col individual-results__col--athlete">
          <div class="e4s-lite-table--standard-text individual-results__athlete--filtered">
            {{ item.row.athlete }}
          </div>
          <div class="individual-results__club">
            {{ item.row.club }}
            <span class="individual-results__meta"
              >({{ item.row.gender }} / {{ item.row.agegroup }})</span
            >
          </div>
        </div>
        <div
          v-for="comp in compColumns"
          :key="comp.compId"
          class="individual-results__col individual-results__col--comp"
        >
          <template
            v-if="
              item.row.pointsByComp[comp.compId] !== undefined &&
              item.row.pointsByComp[comp.compId] !== 0
            "
          >
            <span class="e4s-lite-number e4s-lite-number--points">
              {{ item.row.pointsByComp[comp.compId] }}
            </span>
            <span class="e4s-lite-number e4s-lite-number--score">
              {{ item.row.scoresByComp[comp.compId]?.toFixed(2) }}
            </span>
          </template>
        </div>
        <div class="individual-results__col individual-results__col--total">
          <span class="e4s-lite-number e4s-lite-number--score">{{ item.row.totalPoints }}</span>
        </div>
        <div class="individual-results__col individual-results__col--reason">
          <div
            v-for="reason in item.reasons"
            :key="reason"
            class="individual-results__filter-reason"
          >
            {{ reason }}
          </div>
        </div>
      </div>
    </div>

    <div v-if="showBadData && badDataRows.length > 0" class="individual-results__bad-data">
      <div class="individual-results__bad-data-header">
        Athletes with missing data ({{ badDataRows.length }})
      </div>
      <div
        v-for="item in badDataRows"
        :key="item.row.key"
        class="individual-results__row individual-results__row--bad-data e4s-flex-row e4s-gap--standard e4s-align-items-center"
      >
        <div class="individual-results__col individual-results__col--athlete">
          <div class="e4s-lite-table--standard-text individual-results__athlete--bad-data">
            {{ item.row.athlete }}
          </div>
          <div class="individual-results__club">
            {{ item.row.club }}
            <span class="individual-results__meta"
              >({{ item.row.gender || '—' }} / {{ item.row.agegroup || '—' }})</span
            >
          </div>
        </div>
        <div
          v-for="comp in compColumns"
          :key="comp.compId"
          class="individual-results__col individual-results__col--comp"
        >
          <template
            v-if="
              item.row.pointsByComp[comp.compId] !== undefined &&
              item.row.pointsByComp[comp.compId] !== 0
            "
          >
            <span class="e4s-lite-number e4s-lite-number--points">
              {{ item.row.pointsByComp[comp.compId] }}
            </span>
            <span class="e4s-lite-number e4s-lite-number--score">
              {{ item.row.scoresByComp[comp.compId]?.toFixed(2) }}
            </span>
          </template>
        </div>
        <div class="individual-results__col individual-results__col--total">
          <span class="e4s-lite-number e4s-lite-number--score">{{ item.row.totalPoints }}</span>
        </div>
        <div class="individual-results__col individual-results__col--reason">
          <div
            v-for="issue in item.issues"
            :key="issue"
            class="individual-results__bad-data-reason"
          >
            {{ issue }}
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="!selectedEventGroupId && sortedEventItems.length > 0"
      class="individual-results__empty"
    >
      <p class="e4s-lite-table--standard-text">Please select an event to view individual results</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import {
  fetchIndividualResults,
  processIndividualResults,
  getCompDateColumns,
  type IndividualResultsResponse,
  type AthleteRow
} from './individual-results-service'

import type { IndividualSeriesScheduledItem } from '../store-comp-results'

const props = defineProps<{
  indivSeriesScheduledItems: IndividualSeriesScheduledItem[]
  indivResultsData: Map<number, IndividualResultsResponse>
  indivResultsSelectedEventGroupId: number | null
  indivResultsLoading: boolean
  indivResultsError: string | null
}>()

const emit = defineEmits<{
  'update:selectedEventGroupId': [value: number | null]
  'update:loading': [value: boolean]
  'update:error': [value: string | null]
  'update:resultsData': [value: Map<number, IndividualResultsResponse>]
}>()

const selectedEventGroupId = ref<number | null>(props.indivResultsSelectedEventGroupId ?? null)
const previousEventGroupId = ref<number | null>(props.indivResultsSelectedEventGroupId)
const loading = ref(false)
const error = ref<string | null>(null)
const currentResults = ref<IndividualResultsResponse | null>(null)
const cachedAt = ref<string | null>(null)
const refreshing = ref(false)

interface FilterState {
  gender: 'all' | 'M' | 'F'
  ageGroups: string[]
}

const filterGender = ref<'all' | 'M' | 'F'>('all')
const filterAgeGroups = ref<string[]>([])
const savedFilters = ref<Map<number, FilterState>>(new Map())
const showFilteredOut = ref(false)
const showBadData = ref(false)
const filtersExpanded = ref(typeof window !== 'undefined' ? window.innerWidth >= 768 : false)

const sortedEventItems = computed(() => {
  const unique = new Map()
  for (const item of props.indivSeriesScheduledItems) {
    if (!unique.has(item.eventDefName)) {
      unique.set(item.eventDefName, item)
    }
  }
  return [...unique.values()].sort((a, b) =>
    a.eventDefName.trim().localeCompare(b.eventDefName.trim())
  )
})

const selectedEventId = computed(() => {
  if (!selectedEventGroupId.value) return undefined
  const found = props.indivSeriesScheduledItems.find(
    (item) => item.egId === selectedEventGroupId.value
  )
  return found?.eventId
})

const formattedCachedAt = computed(() => {
  if (!cachedAt.value) return ''
  const date = new Date(cachedAt.value)
  return date.toLocaleString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
})

const formattedCachedAtShort = computed(() => {
  if (!cachedAt.value) return ''
  const date = new Date(cachedAt.value)
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
})

const allRows = computed<AthleteRow[]>(() => {
  if (!currentResults.value) {
    return []
  }
  return processIndividualResults(
    currentResults.value.data.results,
    currentResults.value.data.compDates
  )
})

const availableAgeGroups = computed(() => {
  const groups = new Set<string>()
  for (const row of allRows.value) {
    if (row.agegroup) {
      groups.add(row.agegroup)
    }
  }
  return [...groups].sort()
})

const athleteRows = computed<AthleteRow[]>(() => {
  return allRows.value.filter((row) => {
    const genderMatch = filterGender.value === 'all' || row.gender === filterGender.value
    const ageGroupMatch =
      filterAgeGroups.value.length === 0 || filterAgeGroups.value.includes(row.agegroup)
    return genderMatch && ageGroupMatch
  })
})

const filteredOutRows = computed(() => {
  return allRows.value
    .map((row) => {
      const genderMatch = filterGender.value === 'all' || row.gender === filterGender.value
      const ageGroupMatch =
        filterAgeGroups.value.length === 0 || filterAgeGroups.value.includes(row.agegroup)
      const reasons: string[] = []
      if (!genderMatch) reasons.push(`Gender: ${row.gender} (selected: ${filterGender.value})`)
      if (!ageGroupMatch) reasons.push(`Age group: ${row.agegroup} (not selected)`)
      return { row, reasons, visible: reasons.length === 0 }
    })
    .filter((item) => !item.visible)
})

const badDataRows = computed(() => {
  return allRows.value
    .map((row) => {
      const issues: string[] = []
      if (!row.gender) issues.push('Missing gender')
      if (!row.agegroup) issues.push('Missing age group')
      return { row, issues }
    })
    .filter((item) => item.issues.length > 0)
})

const filtersActive = computed(() => {
  return (
    filterGender.value !== 'all' || filterAgeGroups.value.length < availableAgeGroups.value.length
  )
})

const selectedAgeGroupCount = computed(() => {
  return filterAgeGroups.value.length
})

const allAgeGroupsSelected = computed(() => {
  return (
    availableAgeGroups.value.length > 0 &&
    filterAgeGroups.value.length === availableAgeGroups.value.length
  )
})

const noAgeGroupsSelected = computed(() => {
  return filterAgeGroups.value.length === 0
})

const filterSummary = computed(() => {
  const parts = []
  parts.push(`Gender: ${filterGender.value === 'all' ? 'All' : filterGender.value}`)
  parts.push(`Age: ${filterAgeGroups.value.length}/${availableAgeGroups.value.length}`)
  return parts.join(' | ')
})

const compColumns = computed(() => {
  if (!currentResults.value) {
    return []
  }
  return getCompDateColumns(currentResults.value.data.compDates)
})

function formatDate(dateStr: string): string {
  const date = new Date(dateStr)
  const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' }
  return date.toLocaleDateString(undefined, options)
}

function toggleAgeGroup(ag: string) {
  if (filterAgeGroups.value.includes(ag)) {
    filterAgeGroups.value = filterAgeGroups.value.filter((g) => g !== ag)
  } else {
    filterAgeGroups.value.push(ag)
  }
}

function selectAllAgeGroups() {
  filterAgeGroups.value = [...availableAgeGroups.value]
}

function deselectAllAgeGroups() {
  filterAgeGroups.value = []
}

function clearFilters() {
  filterGender.value = 'all'
  filterAgeGroups.value = [...availableAgeGroups.value]
}

function pruneAgeGroupFilters() {
  const available = new Set(availableAgeGroups.value)
  const preserved = filterAgeGroups.value.filter((ag) => available.has(ag))
  filterAgeGroups.value = preserved.length > 0 ? preserved : [...availableAgeGroups.value]
}

async function refreshResults() {
  if (!selectedEventGroupId.value) {
    return
  }

  refreshing.value = true
  error.value = null

  try {
    const results = await fetchIndividualResults(selectedEventGroupId.value, selectedEventId.value)
    currentResults.value = results
    cachedAt.value = results.timestamp || new Date().toISOString()

    const newCache = new Map(props.indivResultsData)
    newCache.set(selectedEventGroupId.value, results)
    emit('update:resultsData', newCache)
    pruneAgeGroupFilters()
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to refresh results'
  } finally {
    refreshing.value = false
  }
}

function saveCurrentFilters() {
  if (previousEventGroupId.value !== null) {
    savedFilters.value.set(previousEventGroupId.value, {
      gender: filterGender.value,
      ageGroups: [...filterAgeGroups.value]
    })
  }
}

function restoreFiltersForEvent() {
  const saved = savedFilters.value.get(selectedEventGroupId.value!)
  if (saved) {
    filterGender.value = saved.gender
    filterAgeGroups.value = [...saved.ageGroups]
    pruneAgeGroupFilters()
  }
}

function onEventGroupChange() {
  saveCurrentFilters()
  previousEventGroupId.value = selectedEventGroupId.value
  emit('update:selectedEventGroupId', selectedEventGroupId.value)
  if (!selectedEventGroupId.value) {
    currentResults.value = null
    loading.value = false
    error.value = null
    cachedAt.value = null
    filterGender.value = 'all'
    filterAgeGroups.value = []
  } else {
    loadResults(true)
  }
}

async function loadResults(restoreSavedFilters = false) {
  if (!selectedEventGroupId.value) {
    return
  }

  const cached = props.indivResultsData.get(selectedEventGroupId.value)
  if (cached) {
    currentResults.value = cached
    cachedAt.value = cached.timestamp || new Date().toISOString()
    if (restoreSavedFilters) {
      restoreFiltersForEvent()
    } else {
      pruneAgeGroupFilters()
    }
    return
  }

  loading.value = true
  error.value = null

  try {
    const results = await fetchIndividualResults(selectedEventGroupId.value, selectedEventId.value)
    currentResults.value = results
    cachedAt.value = results.timestamp || new Date().toISOString()

    const newCache = new Map(props.indivResultsData)
    newCache.set(selectedEventGroupId.value, results)
    emit('update:resultsData', newCache)
    if (restoreSavedFilters) {
      restoreFiltersForEvent()
    } else {
      pruneAgeGroupFilters()
    }
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to load results'
  } finally {
    loading.value = false
  }
}

watch(
  () => props.indivResultsSelectedEventGroupId,
  (newVal) => {
    selectedEventGroupId.value = newVal
  }
)

watch(
  () => props.indivResultsData,
  () => {
    if (selectedEventGroupId.value) {
      const cached = props.indivResultsData.get(selectedEventGroupId.value)
      if (cached) {
        currentResults.value = cached
        cachedAt.value = cached.timestamp || new Date().toISOString()
        pruneAgeGroupFilters()
      }
    }
  },
  { deep: true }
)

watch(
  availableAgeGroups,
  (newGroups) => {
    if (newGroups.length > 0 && filterAgeGroups.value.length === 0) {
      filterAgeGroups.value = [...newGroups]
    }
  },
  { immediate: true }
)

onMounted(() => {
  // Do not reset selectedEventGroupId - preserve dropdown selection across remounts/navigations
})

onUnmounted(() => {
  // Invalidate cache when navigating away from tab
  emit('update:resultsData', new Map())
})
</script>

<style scoped>
.individual-results {
  width: 100%;
}

.individual-results__header {
  padding: var(--e4s-gap--standard) 0;
  background-color: #e9ecef;
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
  margin-top: var(--e4s-gap--small);
  margin-bottom: var(--e4s-gap--small);
}

.individual-results__col {
  display: flex;
  align-items: center;
  padding: var(--e4s-gap--small-qaz);
}

.individual-results__col--athlete {
  flex: 2;
  min-width: 150px;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.individual-results__club {
  font-size: 0.85em;
  color: #6c757d;
}

.individual-results__meta {
  font-size: 0.85em;
  color: #6c757d;
  margin-left: 4px;
}

.individual-results__col--comp {
  flex: 0 0 80px;
  justify-content: flex-start;
  flex-direction: column;
}

.individual-results__col--total {
  flex: 0 0 80px;
  justify-content: center;
  font-weight: bold;
  text-align: right;
  align-items: flex-start;
}

.individual-results__row {
  padding: var(--e4s-gap--small) 0;
  border-bottom: 1px solid #f0f0f0;
}

.individual-results__row--even {
  background-color: #fafafa;
}

.individual-results__empty,
.individual-results__loading,
.individual-results__error {
  text-align: center;
  padding: var(--e4s-gap--standard);
  color: var(--e4s-color-text-secondary);
}

.individual-results__error {
  color: var(--e4s-color-error, #dc3545);
}

.individual-results__empty-hint {
  font-size: 0.9em;
  margin-top: -8px;
}

.individual-results__event-selector {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 16px;
}

.individual-results__select {
  flex: 1;
  min-height: 44px;
  padding: 10px 12px;
  font-size: 16px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  background-color: #fff;
}

.individual-results__metadata {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 4px;
}

.individual-results__updated {
  font-size: 0.8em;
  color: var(--e4s-color-text-secondary, #6c757d);
}

.individual-results__refresh-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  background: none;
  border: none;
  color: var(--e4s-color-primary, #007bff);
  cursor: pointer;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.individual-results__refresh-btn:hover {
  background-color: rgba(0, 123, 255, 0.1);
}

.individual-results__refresh-btn:disabled {
  color: var(--e4s-color-text-disabled, #aaa);
  cursor: not-allowed;
}

.individual-results__refresh-icon {
  transition: transform 0.5s ease;
}

.individual-results__refresh-icon--spinning {
  animation: individual-results__spin 1s linear infinite;
}

@keyframes individual-results__spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.individual-results__filter-wrapper {
  /* margin-bottom: 16px; */
  padding-bottom: 8px;
  /* background-color: #f0f6ff; */
  /* border-radius: 8px; */
}

.individual-results__filter-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 48px;
  padding: 10px 14px;
  background-color: #e7f1ff;
  border: 2px solid var(--e4s-button--primary__background, #007bff);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.95em;
  color: var(--e4s-button--primary__background, #007bff);
  font-weight: 600;
}

.individual-results__filter-toggle:hover {
  background-color: #d0e4ff;
  border-color: var(--e4s-color-primary-dark, #0056b3);
}

.individual-results__filter-label {
  font-weight: 700;
  font-size: 1em;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--e4s-button--primary__background, #007bff);
  flex-shrink: 0;
}

.individual-results__toggle-icon {
  flex-shrink: 0;
  color: var(--e4s-color-primary, #007bff);
}

.individual-results__filter-summary {
  flex: 1;
  text-align: left;
  font-weight: 500;
}

.individual-results__chevron-icon {
  flex-shrink: 0;
  transition: transform 0.2s;
}

.individual-results__chevron-icon--open {
  transform: rotate(180deg);
}

.individual-results__filters {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  margin-top: 8px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.individual-results__filters--expanded {
  display: flex;
}

.individual-results__filter-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.individual-results__filter-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.individual-results__filter-section-label {
  font-weight: 600;
  font-size: 0.9em;
  color: var(--e4s-color-text-secondary);
}

.individual-results__selected-count {
  font-size: 0.8em;
  color: var(--e4s-color-text-secondary);
}

.individual-results__segmented-group {
  display: flex;
  border: 1px solid #ced4da;
  border-radius: 6px;
  overflow: hidden;
}

.individual-results__segmented-btn {
  flex: 1;
  min-height: 44px;
  padding: 8px 16px;
  font-size: 0.9em;
  font-weight: 500;
  background-color: #fff;
  border: none;
  border-right: 1px solid #ced4da;
  cursor: pointer;
  transition: all 0.2s;
  color: var(--e4s-color-text-secondary);
}

.individual-results__segmented-btn:last-child {
  border-right: none;
}

.individual-results__segmented-btn:hover {
  background-color: #e9ecef;
}

.individual-results__segmented-btn--active {
  background-color: var(--e4s-button--primary__background, #007bff);
  color: #fff;
}

.individual-results__segmented-btn--active:hover {
  background-color: var(--e4s-color-primary-dark, #0056b3);
}

.individual-results__age-group-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.individual-results__age-group-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 12px;
  font-size: 0.85em;
  background-color: #fff;
  border: 1px solid #ced4da;
  border-radius: 18px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.individual-results__age-group-btn:hover {
  background-color: #e9ecef;
}

.individual-results__age-group-btn--active {
  background-color: var(--e4s-button--primary__background, #007bff);
  border-color: var(--e4s-button--primary__background, #007bff);
  color: #fff;
}

.individual-results__age-group-btn--active:hover {
  background-color: var(--e4s-color-primary-dark, #0056b3);
}

.individual-results__check-icon {
  flex-shrink: 0;
}

.individual-results__age-group-label {
  flex: 1;
}

.individual-results__age-group-actions {
  display: flex;
  gap: 8px;
  margin-top: 6px;
}

.individual-results__quick-action-btn {
  flex: 1;
  min-height: 36px;
  padding: 6px 12px;
  font-size: 0.8em;
  background: none;
  border: 1px solid #ced4da;
  border-radius: 4px;
  cursor: pointer;
  color: var(--e4s-color-primary, #007bff);
  transition: all 0.2s;
}

.individual-results__quick-action-btn:hover:not(:disabled) {
  background-color: rgba(0, 123, 255, 0.05);
  border-color: var(--e4s-color-primary, #007bff);
}

.individual-results__quick-action-btn:disabled {
  color: var(--e4s-color-text-disabled, #aaa);
  cursor: not-allowed;
}

.individual-results__actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid #e9ecef;
}

.individual-results__count {
  font-size: 0.85em;
  color: var(--e4s-color-text-secondary);
  text-align: center;
}

.individual-results__clear-btn,
.individual-results__bad-data-toggle {
  width: 100%;
  min-height: 44px;
  padding: 10px 16px;
  font-size: 0.9em;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.individual-results__clear-btn {
  background-color: var(--e4s-color-primary, #007bff);
  color: #fff;
  border: none;
}

.individual-results__clear-btn:hover {
  background-color: var(--e4s-color-primary-dark, #0056b3);
}

.individual-results__bad-data-toggle {
  background: none;
  border: 1px solid #e67e22;
  color: #e67e22;
}

.individual-results__bad-data-toggle:hover {
  background-color: rgba(230, 126, 34, 0.05);
}

.e4s-button--small {
  font-size: 0.75em;
  padding: 2px 6px;
  cursor: pointer;
}

.individual-results__filtered-out {
  margin-top: var(--e4s-gap--standard);
  border: 1px dashed #ccc;
  border-radius: 4px;
  padding: var(--e4s-gap--small);
  background-color: #fafafa;
}

.individual-results__filtered-header {
  font-weight: 600;
  font-size: 0.9em;
  color: #6c757d;
  margin-bottom: var(--e4s-gap--small);
  padding-bottom: 4px;
  border-bottom: 1px solid #e0e0e0;
}

.individual-results__row--filtered {
  opacity: 0.6;
  background-color: #f5f5f5;
}

.individual-results__row--filtered:hover {
  opacity: 0.8;
}

.individual-results__athlete--filtered {
  text-decoration: line-through;
  color: #6c757d;
}

.individual-results__col--reason {
  flex: 0 0 200px;
  font-size: 0.8em;
  color: #c0392b;
}

.individual-results__filter-reason {
  font-size: 0.9em;
  padding: 2px 0;
}

.individual-results__show-bad-data {
  color: #e67e22;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.85em;
  text-decoration: underline;
  padding: 0;
  margin-left: 8px;
}

.individual-results__show-bad-data:hover {
  color: #d35400;
}

.individual-results__bad-data {
  margin-top: var(--e4s-gap--standard);
  border: 1px dashed #e67e22;
  border-radius: 4px;
  padding: var(--e4s-gap--small);
  background-color: #fef9e7;
}

.individual-results__bad-data-header {
  font-weight: 600;
  font-size: 0.9em;
  color: #e67e22;
  margin-bottom: var(--e4s-gap--small);
  padding-bottom: 4px;
  border-bottom: 1px solid #f0e0c0;
}

.individual-results__row--bad-data {
  opacity: 0.7;
  background-color: #fdf5e6;
}

.individual-results__row--bad-data:hover {
  opacity: 0.9;
}

.individual-results__athlete--bad-data {
  color: #e67e22;
  font-style: italic;
}

.individual-results__bad-data-reason {
  font-size: 0.9em;
  color: #c0392b;
  font-weight: 600;
  padding: 2px 0;
}

.e4s-lite-number--points {
  display: block;
  font-size: 1em;
  font-weight: 600;
}

.individual-results__col--comp .e4s-lite-number--score {
  display: block;
  font-size: 0.75em;
  color: #6c757d;
  margin-top: 2px;
}

@media (max-width: 767px) {
  .individual-results__col--athlete {
    min-width: 100px;
  }

  .individual-results__col--comp {
    flex: 0 0 60px;
  }

  .individual-results__col--reason {
    flex: 1 1 100%;
    order: 1;
    margin-top: 4px;
  }
}

@media (min-width: 768px) {
  .individual-results__select {
    min-width: 280px;
  }

  .individual-results__filters {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: flex-start;
  }

  .individual-results__filter-section {
    flex: 1;
    min-width: 200px;
  }

  .individual-results__segmented-group {
    width: fit-content;
  }

  .individual-results__actions {
    flex-direction: row;
    align-items: center;
    width: 100%;
    padding-top: 12px;
    border-top: 1px solid #e9ecef;
    justify-content: space-between;
  }

  .individual-results__count {
    text-align: left;
  }

  .individual-results__clear-btn,
  .individual-results__bad-data-toggle {
    width: auto;
  }
}
</style>
